import { useDispatch, useSelector } from 'react-redux';
import { Button, IllustrativeIcon, Surface, Txt } from '@vst/beam';
import { CheckMark } from '@vst/beam-icons/illustrative-icons';
import { useTranslation } from 'react-i18next';

import { Container } from '@app/components/Container';
import { useScreenResolution } from '@app/hooks';
import { Alert, AlertAction, AlertType, DisplayLocation, setAlerts } from '@app/redux/alerts';
import { selectChangePlan } from '@app/redux/changePlan';

import { NotificationLevel } from '@mfe/shared/schema-types';

import ModemAlert from '../../../components/ModemAlert';
import { NavigationHandlers } from '../../../types';

import styles from './ConfirmationWithoutEquipmentChange.module.scss';

const ConfirmationWithoutEquipmentChange = ({ navigateToOverview }: Pick<NavigationHandlers, 'navigateToOverview'>) => {
  const dispatch = useDispatch();
  const {
    cart: { hasEquipmentForSelfInstall },
  } = useSelector(selectChangePlan);

  const { t } = useTranslation(['ChangePlan', 'Alerts']);

  const { isLarge, isExtraLarge } = useScreenResolution();
  const isDesktop = isLarge || isExtraLarge;

  const description = hasEquipmentForSelfInstall
    ? t('ChangePlan:orderConfirmation.withoutEquipmentChange.altDescription')
    : t('ChangePlan:orderConfirmation.withoutEquipmentChange.description');

  const goToOverview = () => {
    const alerts: Alert[] = [
      {
        level: NotificationLevel.Success,
        title: t('Alerts:changePlan.orderAlert.title'),
        caption: t('Alerts:changePlan.orderAlert.caption'),
        button: {
          action: AlertAction.Refresh,
          label: t('Alerts:changePlan.orderAlert.button.text'),
        },
        displayLocation: DisplayLocation.LOCAL,
        type: AlertType.ChangePlanSuccess,
      },
    ];

    dispatch(setAlerts(alerts));
    navigateToOverview();
  };

  return (
    <Container>
      <Surface
        className={styles['container']}
        my={{ xs: undefined, sm: '40px' }}
        px={{ xs: '16px', sm: '24px', md: '40px' }}
        radius="16px"
      >
        <IllustrativeIcon data-cy="CheckMark-Icon" name="CheckMark" icon={CheckMark} color="green_600" size={95} />
        <Txt data-cy="orderConfirmation-title" mt="48px" mb="12px" variant="bodyLargeBold">
          {t('ChangePlan:orderConfirmation.withoutEquipmentChange.title')}
        </Txt>
        <Txt data-cy="orderConfirmation-description" mb="40px" variant="bodyLargeRegular" color="subtle">
          {description}
        </Txt>
        <div data-cy="orderConfirmation-Alert">
          <ModemAlert />
        </div>
        <Button
          className={styles['backToMyViasat']}
          data-cy="orderConfirmation-button"
          fluid
          onClick={goToOverview}
          mt={isDesktop ? undefined : '40px'}
        >
          {t('ChangePlan:orderConfirmation.withoutEquipmentChange.button')}
        </Button>
      </Surface>
    </Container>
  );
};

export default ConfirmationWithoutEquipmentChange;
